import { useCallback, useMemo, useState } from 'react';

import { emptyObject } from 'ui-components/default-props/empty';

const buildRecommendationKey = (fromSellerProviderId, fromInsuranceProviderId, toInsuranceProviderId) => {
  return `${fromSellerProviderId}-${fromInsuranceProviderId}-${toInsuranceProviderId}`;
};

/**
 * @param {InsuranceRateRecommendationSummary[]} summaryInsuranceRecommendations
 * @returns {CostReviewInsuranceRecommendationMap}
 */
const buildInsuranceRecommendations = (summaryInsuranceRecommendations) => {
  return summaryInsuranceRecommendations?.reduce((acc, rec) => {
    const { fromInsuranceProviderId, toInsuranceProviderId, totalSavings, fromSellerProviderId } = rec;

    return {
      ...acc,
      [fromSellerProviderId]: {
        key: buildRecommendationKey(fromSellerProviderId, fromInsuranceProviderId, toInsuranceProviderId),
        fromSellerProviderId: `${fromSellerProviderId}`,
        fromInsuranceProviderId: `${fromInsuranceProviderId}`,
        toInsuranceProviderId: `${toInsuranceProviderId}`,
        totalSavings,
      },
    };
  }, {});
};

/**
 * @typedef {object} CostReviewInsuranceRecommendations
 * @property {CostReviewSelectedRecommendations} selectedInsuranceRecommendations
 * @property {CostReviewInsuranceRecommendationMap} insuranceRecommendations
 * @property {(recommendationKey: string) => void} toggleInsuranceRecommendation
 */

/**
 * @param {InsuranceRateRecommendationSummary[]} insuranceRecommendationSummary
 * @returns {CostReviewInsuranceRecommendations}
 */
export const useCostReviewInsuranceRecommendations = (insuranceRecommendationSummary) => {
  const [selectedInsuranceRecommendations, setSelectedInsuranceRecommendations] = useState({});

  const insuranceRecommendations = useMemo(() => {
    return insuranceRecommendationSummary ? buildInsuranceRecommendations(insuranceRecommendationSummary) : emptyObject;
  }, [insuranceRecommendationSummary]);

  const toggleInsuranceRecommendation = useCallback((recommendationKey) => {
    setSelectedInsuranceRecommendations((prev) => {
      return {
        ...prev,
        [recommendationKey]: !prev[recommendationKey],
      };
    });
  }, []);

  const selectAllInsuranceRecommendations = useCallback(() => {
    const allRecommendationsSelected = insuranceRecommendationSummary.reduce((acc, recommendation) => {
      const recommendationKey = buildRecommendationKey(
        recommendation.fromSellerProviderId,
        recommendation.fromInsuranceProviderId,
        recommendation.toInsuranceProviderId,
      );
      return {
        ...acc,
        [recommendationKey]: true,
      };
    }, {});

    setSelectedInsuranceRecommendations(allRecommendationsSelected);
  }, [insuranceRecommendationSummary]);

  return {
    selectedInsuranceRecommendations,
    insuranceRecommendations,
    toggleInsuranceRecommendation,
    selectAllInsuranceRecommendations,
  };
};
